/* =============================================================
   GlobalSearch.jsx — Admin portal header search bar
   -------------------------------------------------------------
   Searches across incoming students, outgoing students, and
   partner universities. Results appear in a dropdown with
   match highlighting and navigate to the relevant profile page.

   Data source: Supabase (live data for the logged-in university).

   Cache strategy (module-level object, 5-min TTL):
     All students + partners are loaded once on mount and filtered
     locally on every keystroke — no per-query Supabase calls.
     Cache survives component unmount/remount and token refresh.
   ============================================================= */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase }    from '../lib/supabase';
import { useAuth }     from '../context/AuthContext';


/* ─── MODULE-LEVEL CACHE ─────────────────────────────────────── */
const _cache = {
  data:         null,   // { incoming: [], outgoing: [], partners: [] }
  universityId: null,
  fetchedAt:    0,
};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes


/* ─── HIGHLIGHT HELPER ───────────────────────────────────────── */
// Wraps the matching substring in a styled <mark> element.
function highlight(text, query) {
  if (!text || !query) return text ?? '';
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-indigo-100 text-indigo-700 rounded px-0.5 not-italic font-semibold">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}


/* ─── COMPONENT ──────────────────────────────────────────────── */
export default function GlobalSearch() {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const wrapRef   = useRef(null);
  const inputRef  = useRef(null);

  const [query,    setQuery]    = useState('');
  const [open,     setOpen]     = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [allData,  setAllData]  = useState(
    _cache.data ?? { incoming: [], outgoing: [], partners: [] }
  );
  const [results, setResults] = useState({ incoming: [], outgoing: [], partners: [] });


  /* ── Load all data once (cached) ───────────────────────────── */
  const loadData = useCallback(async () => {
    if (!user?.universityId) return;

    const cacheHit =
      _cache.data &&
      _cache.universityId === user.universityId &&
      (Date.now() - _cache.fetchedAt) < CACHE_TTL;

    if (cacheHit) {
      setAllData(_cache.data);
      return;
    }

    setLoading(true);
    try {
      const [studentsRes, partnersRes] = await Promise.all([
        supabase
          .from('students')
          .select('id, name, type, home_university, destination_university, country')
          .eq('university_id', user.universityId),

        supabase
          .from('partner_universities')
          .select('id, name, abbreviation, country, type')
          .eq('university_id', user.universityId),
      ]);

      const students = studentsRes.data ?? [];
      const data = {
        incoming: students.filter(s => s.type === 'incoming'),
        outgoing: students.filter(s => s.type === 'outgoing'),
        partners: partnersRes.data ?? [],
      };

      _cache.data         = data;
      _cache.universityId = user.universityId;
      _cache.fetchedAt    = Date.now();

      setAllData(data);
    } catch (err) {
      console.error('[GlobalSearch] fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.universityId]);

  useEffect(() => { loadData(); }, [loadData]);


  /* ── Filter locally on every keystroke ─────────────────────── */
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) { setResults({ incoming: [], outgoing: [], partners: [] }); return; }

    setResults({
      incoming: allData.incoming.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.home_university?.toLowerCase().includes(q) ||
        s.country?.toLowerCase().includes(q)
      ).slice(0, 4),

      outgoing: allData.outgoing.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.destination_university?.toLowerCase().includes(q) ||
        s.country?.toLowerCase().includes(q)
      ).slice(0, 4),

      partners: allData.partners.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.abbreviation?.toLowerCase().includes(q) ||
        p.country?.toLowerCase().includes(q)
      ).slice(0, 3),
    });
  }, [query, allData]);


  /* ── Close on outside click ────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Close on ESC ──────────────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);


  const hasResults =
    results.incoming.length > 0 ||
    results.outgoing.length > 0 ||
    results.partners.length > 0;

  const noResults = query.trim().length >= 2 && !hasResults && !loading;

  const go = (path) => {
    navigate(path);
    setOpen(false);
    setQuery('');
  };


  return (
    <div ref={wrapRef} className="relative flex-1 max-w-sm">

      {/* Search input */}
      <div className="relative">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search students, universities..."
          className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">close</span>
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {open && query.trim().length >= 1 && (
        <div className="absolute top-full mt-2 left-0 w-[420px] bg-white border border-slate-100 rounded-2xl shadow-xl shadow-slate-200/60 z-50 overflow-hidden">

          {/* Loading state */}
          {loading && (
            <div className="px-5 py-6 flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4 text-indigo-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              <span className="text-sm text-slate-400">Loading…</span>
            </div>
          )}

          {/* Empty state */}
          {noResults && (
            <div className="px-5 py-8 text-center">
              <span className="material-symbols-outlined text-slate-300 text-[32px]">search_off</span>
              <p className="text-sm text-slate-400 mt-2">No results for "<span className="font-medium text-slate-500">{query}</span>"</p>
            </div>
          )}

          {/* Incoming students section */}
          {results.incoming.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Incoming Students</p>
              {results.incoming.map(s => (
                <button
                  key={s.id}
                  onClick={() => go(`/incoming/${s.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left group"
                >
                  <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {s.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0b1c30] leading-tight">{highlight(s.name, query)}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {highlight(s.home_university, query)} · {s.country?.replace(/[^\x00-\x7F]/g, '').trim()}
                    </p>
                  </div>
                  <span className="material-symbols-outlined text-slate-300 group-hover:text-indigo-400 text-[16px] ml-auto flex-shrink-0 transition-colors">arrow_forward</span>
                </button>
              ))}
            </div>
          )}

          {/* Outgoing students section */}
          {results.outgoing.length > 0 && (
            <div className={results.incoming.length > 0 ? 'border-t border-slate-50' : ''}>
              <p className="px-4 pt-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Outgoing Students</p>
              {results.outgoing.map(s => (
                <button
                  key={s.id}
                  onClick={() => go(`/outgoing/${s.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left group"
                >
                  <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {s.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0b1c30] leading-tight">{highlight(s.name, query)}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {highlight(s.destination_university, query)} · {s.country?.replace(/[^\x00-\x7F]/g, '').trim()}
                    </p>
                  </div>
                  <span className="material-symbols-outlined text-slate-300 group-hover:text-indigo-400 text-[16px] ml-auto flex-shrink-0 transition-colors">arrow_forward</span>
                </button>
              ))}
            </div>
          )}

          {/* Partners section */}
          {results.partners.length > 0 && (
            <div className={(results.incoming.length > 0 || results.outgoing.length > 0) ? 'border-t border-slate-50' : ''}>
              <p className="px-4 pt-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Partner Universities</p>
              {results.partners.map(p => (
                <button
                  key={p.id}
                  onClick={() => go('/partners')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left group"
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 text-[10px] font-bold flex-shrink-0">
                    {p.abbreviation}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0b1c30] leading-tight">{highlight(p.name, query)}</p>
                    <p className="text-xs text-slate-400">{p.country?.replace(/[^\x00-\x7F]/g, '').trim()} · {p.type}</p>
                  </div>
                  <span className="material-symbols-outlined text-slate-300 group-hover:text-indigo-400 text-[16px] ml-auto flex-shrink-0 transition-colors">arrow_forward</span>
                </button>
              ))}
            </div>
          )}

          {/* Footer hint */}
          {hasResults && (
            <div className="px-4 py-2 border-t border-slate-50 flex items-center gap-1">
              <span className="text-[10px] text-slate-300">Press</span>
              <kbd className="text-[10px] bg-slate-100 text-slate-400 px-1 py-0.5 rounded font-mono">ESC</kbd>
              <span className="text-[10px] text-slate-300">to close</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
